import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { EnhancedDAWStudio } from '../components/DAWComponents';

// ============================================================================
// FUNCTIONAL PROGRAMMING UTILITIES
// ============================================================================

// Pure function: Compose multiple transformations
const pipe = (...fns) => (x) => fns.reduce((v, f) => f(v), x);

// Pure function: Identity
const identity = (x) => x;

// Pure function: Clamp value between min and max
const clamp = (min, max) => (value) => Math.min(max, Math.max(min, value));

// Pure function: Deep merge objects immutably
const deepMerge = (target, source) => {
  if (!source) return target;
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

// ============================================================================
// STUDIO MODE DEFINITIONS
// ============================================================================

const STUDIO_MODES = {
  CHOOSE: 'choose',
  DAW: 'daw',           // Digital Audio Workstation
  VIDEO: 'video',       // Video Editor with live filters
  RECORD: 'record',     // Quick video recording
  UPLOAD: 'upload',     // File upload
  GREEN_SCREEN: 'green-screen',
  PREVIEW: 'preview',
};

const MODE_CONFIG = {
  [STUDIO_MODES.DAW]: {
    label: 'DAW Studio',
    description: 'Audio production & music creation',
    icon: '🎵',
    color: 'from-purple-500 to-violet-600',
    features: ['Multi-track audio', 'Effects rack', 'MIDI support', 'Sampling'],
  },
  [STUDIO_MODES.VIDEO]: {
    label: 'Video Editor',
    description: 'Full video editing suite with live filters',
    icon: '🎬',
    color: 'from-blue-500 to-cyan-600',
    features: ['Timeline editing', 'Filters & effects', 'Color grading', 'Transitions'],
  },
  [STUDIO_MODES.RECORD]: {
    label: 'Quick Record',
    description: 'Record video with camera controls',
    icon: '📹',
    color: 'from-red-500 to-pink-600',
    features: ['Pro camera', 'Resolution options', 'Countdown timer', 'Guidelines'],
  },
  [STUDIO_MODES.UPLOAD]: {
    label: 'Upload',
    description: 'Upload existing media files',
    icon: '📤',
    color: 'from-green-500 to-emerald-600',
    features: ['Video upload', 'Batch import', 'Format conversion'],
  },
  [STUDIO_MODES.GREEN_SCREEN]: {
    label: 'Green Screen',
    description: 'Chroma key compositing studio',
    icon: '🟩',
    color: 'from-teal-500 to-green-600',
    features: ['Background removal', 'Virtual backgrounds', 'Compositing'],
  },
};

// ============================================================================
// LONG PRESS HOOK (Functional Approach)
// ============================================================================

const useLongPress = (callback, { threshold = 500, onShortPress } = {}) => {
  const timerRef = useRef(null);
  const isLongPressRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  const start = useCallback((event) => {
    isLongPressRef.current = false;
    
    // Store start position for movement detection
    const touch = event.touches?.[0] || event;
    startPosRef.current = { x: touch.clientX, y: touch.clientY };
    
    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      callback(event);
    }, threshold);
  }, [callback, threshold]);

  const move = useCallback((event) => {
    if (!timerRef.current) return;
    
    const touch = event.touches?.[0] || event;
    const dx = Math.abs(touch.clientX - startPosRef.current.x);
    const dy = Math.abs(touch.clientY - startPosRef.current.y);
    
    // Cancel if moved more than 10px
    if (dx > 10 || dy > 10) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const end = useCallback((event) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      
      // Trigger short press if not a long press
      if (!isLongPressRef.current && onShortPress) {
        onShortPress(event);
      }
    }
  }, [onShortPress]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isLongPressRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    onMouseDown: start,
    onMouseMove: move,
    onMouseUp: end,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchMove: move,
    onTouchEnd: end,
  };
};

// ============================================================================
// STUDIO MODE SELECTOR (Long Press Radial Menu)
// ============================================================================

const StudioModeSelector = ({ isOpen, onSelect, onClose, anchorPosition }) => {
  if (!isOpen) return null;

  const modes = [
    STUDIO_MODES.DAW,
    STUDIO_MODES.VIDEO,
    STUDIO_MODES.RECORD,
    STUDIO_MODES.UPLOAD,
    STUDIO_MODES.GREEN_SCREEN,
  ];

  // Calculate radial positions
  const radius = 120;
  const startAngle = -90; // Start from top
  const angleStep = 72; // 360 / 5 modes

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Radial menu centered on anchor */}
      <div 
        className="absolute"
        style={{
          left: anchorPosition?.x || '50%',
          top: anchorPosition?.y || '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        {/* Center indicator */}
        <div className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
          <span className="text-2xl">✨</span>
        </div>

        {/* Mode buttons arranged radially */}
        {modes.map((mode, index) => {
          const config = MODE_CONFIG[mode];
          const angle = ((startAngle + index * angleStep) * Math.PI) / 180;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;

          return (
            <button
              key={mode}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(mode);
              }}
              className={`absolute w-20 h-20 rounded-2xl bg-gradient-to-br ${config.color} 
                         flex flex-col items-center justify-center gap-1
                         transform transition-all duration-300 hover:scale-110
                         shadow-lg shadow-black/30 border border-white/20`}
              style={{
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <span className="text-2xl">{config.icon}</span>
              <span className="text-[10px] font-medium text-white/90 leading-tight text-center">
                {config.label}
              </span>
            </button>
          );
        })}

        {/* Instruction text */}
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 text-center">
          <p className="text-white/60 text-sm">Select a studio mode</p>
          <p className="text-white/40 text-xs mt-1">Tap outside to cancel</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN STUDIO COMPONENT
// ============================================================================

export function Studio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState(STUDIO_MODES.CHOOSE);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [selectorAnchor, setSelectorAnchor] = useState(null);

  // Long press handler to show radial menu
  const handleLongPress = useCallback((event) => {
    const touch = event.touches?.[0] || event;
    setSelectorAnchor({ x: touch.clientX, y: touch.clientY });
    setShowModeSelector(true);
    
    // Haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  }, []);

  // Short press handler for quick record
  const handleShortPress = useCallback(() => {
    setMode(STUDIO_MODES.RECORD);
  }, []);

  const longPressHandlers = useLongPress(handleLongPress, {
    threshold: 400,
    onShortPress: handleShortPress,
  });

  // Mode selection from radial menu
  const handleModeSelect = useCallback((selectedMode) => {
    setShowModeSelector(false);
    setMode(selectedMode);
  }, []);

  // Auth redirect
  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/studio', { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    return (
      <div className="h-screen w-full bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <p className="text-2xl font-semibold mb-3">Sign in required</p>
        <p className="text-gray-400 max-w-md">
          You need an active profile to access the Studio. Redirecting you to the login page…
        </p>
      </div>
    );
  }

  // ========================================
  // RENDER: Choose Mode
  // ========================================
  if (mode === STUDIO_MODES.CHOOSE) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center px-4 relative">
        {/* Mode Selector Overlay */}
        <StudioModeSelector
          isOpen={showModeSelector}
          onSelect={handleModeSelect}
          onClose={() => setShowModeSelector(false)}
          anchorPosition={selectorAnchor}
        />

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3">Studio</h1>
          <p className="text-gray-400">Create, edit, and produce content</p>
        </div>

        {/* Main Action Area */}
        <div className="w-full max-w-md space-y-4">
          {/* Big Studio Button - Long press for menu, tap for quick record */}
          <div className="relative">
            <button
              {...longPressHandlers}
              className="w-full py-8 px-8 rounded-3xl bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 
                         text-white font-bold text-xl flex items-center justify-center gap-4
                         shadow-2xl shadow-purple-500/30 border border-white/10
                         transition-all duration-200 active:scale-95
                         select-none touch-none"
            >
              <span className="text-4xl">🎬</span>
              <div className="text-left">
                <div className="text-lg font-bold">Open Studio</div>
                <div className="text-sm font-normal text-white/70">
                  Hold for modes • Tap for quick record
                </div>
              </div>
            </button>
            
            {/* Ripple indicator for long press */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 rounded-3xl border-2 border-white/20 animate-pulse" />
            </div>
          </div>

          {/* Mode Cards Grid */}
          <div className="grid grid-cols-2 gap-3 mt-6">
            {Object.entries(MODE_CONFIG).map(([modeKey, config]) => (
              <button
                key={modeKey}
                onClick={() => setMode(modeKey)}
                className={`p-4 rounded-2xl bg-gradient-to-br ${config.color} 
                           text-white text-left transition-all hover:scale-105
                           border border-white/10 shadow-lg`}
              >
                <span className="text-2xl mb-2 block">{config.icon}</span>
                <div className="font-semibold text-sm">{config.label}</div>
                <div className="text-[10px] text-white/70 mt-1 leading-tight">
                  {config.description}
                </div>
              </button>
            ))}
          </div>

          {/* Cancel */}
          <button
            onClick={() => navigate('/')}
            className="w-full py-4 text-gray-400 hover:text-white transition-colors mt-4"
          >
            Cancel
          </button>
        </div>

        {/* Tips */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <p className="text-white/30 text-xs">
            💡 Long-press the Studio button for quick mode selection
          </p>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER: DAW Mode - Enhanced Audio Editor
  // ========================================
  if (mode === STUDIO_MODES.DAW) {
    return (
      <EnhancedDAWStudio 
        onBack={() => setMode(STUDIO_MODES.CHOOSE)} 
        onSave={async (file) => {
          const title = prompt('Enter project name:', 'My New Beat');
          if (!title) return;
          
          try {
             await api.saveAudioProject(file, {
                title,
                is_public: false
             });
             alert('Project saved to profile!');
          } catch (e) {
             console.error(e);
             alert('Failed to save project: ' + e.message);
          }
        }}
      />
    );
  }

  // ========================================
  // RENDER: Video Editor Mode
  // ========================================
  if (mode === STUDIO_MODES.VIDEO) {
    return <VideoEditor onBack={() => setMode(STUDIO_MODES.CHOOSE)} />;
  }

  // ========================================
  // RENDER: Quick Record Mode (Legacy Upload behavior)
  // ========================================
  if (mode === STUDIO_MODES.RECORD) {
    // This will use the existing recording logic
    return <QuickRecord onBack={() => setMode(STUDIO_MODES.CHOOSE)} />;
  }

  // ========================================
  // RENDER: Upload Mode
  // ========================================
  if (mode === STUDIO_MODES.UPLOAD) {
    return <FileUpload onBack={() => setMode(STUDIO_MODES.CHOOSE)} />;
  }

  // ========================================
  // RENDER: Green Screen Mode
  // ========================================
  if (mode === STUDIO_MODES.GREEN_SCREEN) {
    // Import existing GreenScreenStudio
    const GreenScreenStudio = React.lazy(() => 
      import('../components/GreenScreenStudio')
    );
    
    return (
      <React.Suspense fallback={<div className="h-screen bg-black" />}>
        <GreenScreenStudio onBack={() => setMode(STUDIO_MODES.CHOOSE)} />
      </React.Suspense>
    );
  }

  return null;
}

// ============================================================================
// DAW STUDIO COMPONENT
// ============================================================================

const DAWStudio = ({ onBack }) => {
  const [tracks, setTracks] = useState([
    { id: '1', name: 'Track 1', type: 'audio', volume: 1, pan: 0, muted: false, soloed: false, clips: [] },
  ]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [bpm, setBpm] = useState(120);
  const [showEffectsRack, setShowEffectsRack] = useState(false);

  // Pure function: Add track immutably
  const addTrack = useCallback(() => {
    setTracks(prevTracks => [
      ...prevTracks,
      {
        id: Date.now().toString(),
        name: `Track ${prevTracks.length + 1}`,
        type: 'audio',
        volume: 1,
        pan: 0,
        muted: false,
        soloed: false,
        clips: [],
      },
    ]);
  }, []);

  // Pure function: Update track property immutably
  const updateTrack = useCallback((trackId, updates) => {
    setTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId ? { ...track, ...updates } : track
      )
    );
  }, []);

  // Pure function: Remove track immutably
  const removeTrack = useCallback((trackId) => {
    setTracks(prevTracks => prevTracks.filter(track => track.id !== trackId));
  }, []);

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header / Transport */}
      <div className="bg-gray-800 border-b border-gray-700 p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center hover:bg-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">🎵 DAW Studio</h1>
        </div>

        {/* Transport Controls */}
        <div className="flex items-center gap-2">
          <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">
            <span>⏮</span>
          </button>
          <button 
            onClick={() => setIsPlaying(!isPlaying)}
            className={`p-3 rounded-full ${isPlaying ? 'bg-red-500' : 'bg-green-500'} hover:opacity-80`}
          >
            <span className="text-xl">{isPlaying ? '⏸' : '▶'}</span>
          </button>
          <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">
            <span>⏹</span>
          </button>
          <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">
            <span>⏺</span>
          </button>
        </div>

        {/* BPM */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">BPM</span>
          <input
            type="number"
            value={bpm}
            onChange={(e) => setBpm(clamp(40, 300)(parseInt(e.target.value) || 120))}
            className="w-16 bg-gray-700 rounded px-2 py-1 text-center"
          />
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Track List */}
        <div className="w-48 bg-gray-800 border-r border-gray-700 overflow-y-auto">
          <div className="p-2 border-b border-gray-700 flex justify-between items-center">
            <span className="text-xs text-gray-400 uppercase">Tracks</span>
            <button 
              onClick={addTrack}
              className="text-xs bg-purple-600 hover:bg-purple-500 px-2 py-1 rounded"
            >
              + Add
            </button>
          </div>
          
          {tracks.map((track) => (
            <div 
              key={track.id}
              className="p-2 border-b border-gray-700 hover:bg-gray-700/50"
            >
              <div className="flex items-center justify-between mb-2">
                <input
                  type="text"
                  value={track.name}
                  onChange={(e) => updateTrack(track.id, { name: e.target.value })}
                  className="bg-transparent text-sm font-medium w-24 truncate"
                />
                <button
                  onClick={() => removeTrack(track.id)}
                  className="text-gray-500 hover:text-red-400 text-xs"
                >
                  ✕
                </button>
              </div>
              
              {/* Track controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => updateTrack(track.id, { muted: !track.muted })}
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    track.muted ? 'bg-red-500' : 'bg-gray-600'
                  }`}
                >
                  M
                </button>
                <button
                  onClick={() => updateTrack(track.id, { soloed: !track.soloed })}
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    track.soloed ? 'bg-yellow-500' : 'bg-gray-600'
                  }`}
                >
                  S
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={track.volume}
                  onChange={(e) => updateTrack(track.id, { volume: parseFloat(e.target.value) })}
                  className="w-16 h-1 accent-purple-500"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-auto bg-gray-850 relative">
          {/* Time ruler */}
          <div className="h-8 bg-gray-800 border-b border-gray-700 flex items-center sticky top-0 z-10">
            {Array.from({ length: 20 }).map((_, i) => (
              <div 
                key={i} 
                className="w-24 text-xs text-gray-500 border-r border-gray-700 px-2"
              >
                {i + 1}
              </div>
            ))}
          </div>

          {/* Track lanes */}
          {tracks.map((track) => (
            <div 
              key={track.id}
              className="h-20 border-b border-gray-700 relative"
              style={{ opacity: track.muted ? 0.5 : 1 }}
            >
              {/* Empty lane - drop zone for clips */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-gray-600 text-xs">
                  Drag audio files here
                </span>
              </div>
            </div>
          ))}

          {/* Playhead */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20"
            style={{ left: `${currentTime * 24}px` }}
          />
        </div>

        {/* Effects Rack */}
        {showEffectsRack && (
          <div className="w-64 bg-gray-800 border-l border-gray-700 p-3">
            <h3 className="text-sm font-semibold mb-3">Effects Rack</h3>
            <div className="space-y-2">
              {['EQ', 'Compressor', 'Reverb', 'Delay'].map((effect) => (
                <button
                  key={effect}
                  className="w-full p-2 bg-gray-700 rounded text-sm hover:bg-gray-600"
                >
                  {effect}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-800 border-t border-gray-700 p-2 flex justify-between items-center">
        <span className="text-xs text-gray-500">
          {tracks.length} tracks • {bpm} BPM • 48kHz
        </span>
        <button
          onClick={() => setShowEffectsRack(!showEffectsRack)}
          className={`text-xs px-3 py-1 rounded ${
            showEffectsRack ? 'bg-purple-600' : 'bg-gray-700'
          }`}
        >
          Effects Rack
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// VIDEO EDITOR COMPONENT
// ============================================================================

const VideoEditor = ({ onBack }) => {
  const [clips, setClips] = useState([]);
  const [selectedClip, setSelectedClip] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Video adjustment state
  const [adjustments, setAdjustments] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
    hue: 0,
    sharpness: 0,
    blur: 0,
    temperature: 0,
    tint: 0,
    highlights: 0,
    shadows: 0,
    speed: 1,
  });

  // Pure function: Update adjustment immutably
  const updateAdjustment = useCallback((key, value) => {
    setAdjustments(prev => ({ ...prev, [key]: value }));
  }, []);

  // Reset all adjustments
  const resetAdjustments = useCallback(() => {
    setAdjustments({
      brightness: 0,
      contrast: 0,
      saturation: 0,
      hue: 0,
      sharpness: 0,
      blur: 0,
      temperature: 0,
      tint: 0,
      highlights: 0,
      shadows: 0,
      speed: 1,
    });
  }, []);

  const FILTER_CONTROLS = [
    { key: 'brightness', label: 'Brightness', icon: '☀️', min: -100, max: 100 },
    { key: 'contrast', label: 'Contrast', icon: '◐', min: -100, max: 100 },
    { key: 'saturation', label: 'Saturation', icon: '🎨', min: -100, max: 100 },
    { key: 'hue', label: 'Hue', icon: '🌈', min: -180, max: 180 },
    { key: 'sharpness', label: 'Sharpness', icon: '🔪', min: 0, max: 100 },
    { key: 'blur', label: 'Blur', icon: '💫', min: 0, max: 100 },
    { key: 'temperature', label: 'Temperature', icon: '🌡️', min: -100, max: 100 },
    { key: 'tint', label: 'Tint', icon: '💜', min: -100, max: 100 },
    { key: 'highlights', label: 'Highlights', icon: '✨', min: -100, max: 100 },
    { key: 'shadows', label: 'Shadows', icon: '🌑', min: -100, max: 100 },
    { key: 'speed', label: 'Speed', icon: '⏩', min: 0.25, max: 4, step: 0.25, suffix: 'x' },
  ];

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center hover:bg-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold">🎬 Video Editor</h1>
        </div>

        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 bg-gray-700 rounded text-sm hover:bg-gray-600">
            Undo
          </button>
          <button className="px-3 py-1.5 bg-gray-700 rounded text-sm hover:bg-gray-600">
            Redo
          </button>
          <button className="px-4 py-1.5 bg-blue-600 rounded text-sm hover:bg-blue-500 font-medium">
            Export
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Preview Panel */}
        <div className="flex-1 flex flex-col">
          {/* Video Preview */}
          <div className="flex-1 bg-black flex items-center justify-center relative">
            <div 
              className="w-full max-w-2xl aspect-video bg-gray-800 rounded-lg flex items-center justify-center"
              style={{
                filter: `
                  brightness(${100 + adjustments.brightness}%)
                  contrast(${100 + adjustments.contrast}%)
                  saturate(${100 + adjustments.saturation}%)
                  hue-rotate(${adjustments.hue}deg)
                  blur(${adjustments.blur / 10}px)
                `
              }}
            >
              <span className="text-gray-500">Import media to start editing</span>
            </div>

            {/* Import overlay */}
            {clips.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <button className="px-6 py-3 bg-blue-600 rounded-lg font-medium mb-2 pointer-events-auto hover:bg-blue-500">
                    Import Media
                  </button>
                  <p className="text-gray-500 text-sm">or drag files here</p>
                </div>
              </div>
            )}
          </div>

          {/* Transport Controls */}
          <div className="bg-gray-800 border-t border-gray-700 p-2 flex items-center justify-center gap-4">
            <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">⏮</button>
            <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">⏪</button>
            <button 
              onClick={() => setIsPlaying(!isPlaying)}
              className={`p-3 rounded-full ${isPlaying ? 'bg-red-500' : 'bg-green-500'}`}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">⏩</button>
            <button className="p-2 rounded bg-gray-700 hover:bg-gray-600">⏭</button>
            
            <span className="text-sm font-mono text-gray-400 ml-4">
              00:00:00.00 / 00:00:00.00
            </span>
          </div>

          {/* Timeline */}
          <div className="h-32 bg-gray-850 border-t border-gray-700">
            <div className="h-6 bg-gray-800 border-b border-gray-700 flex items-center px-2">
              <span className="text-xs text-gray-500">Timeline</span>
            </div>
            <div className="h-full flex items-center justify-center text-gray-600 text-sm">
              Add clips to the timeline to start editing
            </div>
          </div>
        </div>

        {/* Filters & Adjustments Panel */}
        {showFilters && (
          <div className="w-72 bg-gray-800 border-l border-gray-700 overflow-y-auto">
            <div className="p-3 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10">
              <h3 className="text-sm font-semibold">Adjustments</h3>
              <button 
                onClick={resetAdjustments}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Reset All
              </button>
            </div>

            <div className="p-3 space-y-4">
              {FILTER_CONTROLS.map((control) => (
                <div key={control.key}>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs text-gray-400 flex items-center gap-1.5">
                      <span>{control.icon}</span>
                      <span>{control.label}</span>
                    </label>
                    <span className="text-xs font-mono text-gray-500">
                      {adjustments[control.key]}{control.suffix || ''}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={control.min}
                    max={control.max}
                    step={control.step || 1}
                    value={adjustments[control.key]}
                    onChange={(e) => updateAdjustment(control.key, parseFloat(e.target.value))}
                    className="w-full h-1 accent-blue-500"
                  />
                </div>
              ))}
            </div>

            {/* Transitions */}
            <div className="p-3 border-t border-gray-700">
              <h3 className="text-sm font-semibold mb-3">Transitions</h3>
              <div className="grid grid-cols-3 gap-2">
                {['Cut', 'Fade', 'Wipe', 'Slide', 'Zoom', 'Spin'].map((transition) => (
                  <button
                    key={transition}
                    className="p-2 bg-gray-700 rounded text-xs hover:bg-gray-600"
                  >
                    {transition}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// QUICK RECORD COMPONENT (Simplified from Upload.jsx)
// ============================================================================

const QuickRecord = ({ onBack }) => {
  // This would contain the recording logic from Upload.jsx
  // For now, redirect to the existing Upload component
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to existing upload page with record mode
    navigate('/upload?mode=record');
  }, [navigate]);

  return (
    <div className="h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Opening camera...</p>
      </div>
    </div>
  );
};

// ============================================================================
// FILE UPLOAD COMPONENT
// ============================================================================

const FileUpload = ({ onBack }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList).filter(
      file => file.type.startsWith('video/') || file.type.startsWith('audio/')
    );
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">Upload Media</h1>
      </div>

      {/* Drop Zone */}
      <div className="flex-1 p-4 overflow-auto">
        <div
          className="border-2 border-dashed border-gray-700 rounded-2xl p-8 text-center hover:border-gray-500 transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="text-4xl mb-4">📤</div>
          <p className="text-lg font-medium mb-2">Drag & drop files here</p>
          <p className="text-gray-500 text-sm mb-4">or</p>
          <label className="px-6 py-3 bg-blue-600 rounded-lg font-medium cursor-pointer hover:bg-blue-500">
            Browse Files
            <input
              type="file"
              accept="video/*,audio/*"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
            />
          </label>
          <p className="text-gray-600 text-xs mt-4">
            Supports MP4, MOV, WebM, MP3, WAV • Max 500MB per file
          </p>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-6 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400">Selected Files ({files.length})</h3>
            {files.map((file, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg"
              >
                <div className="w-12 h-12 bg-gray-700 rounded flex items-center justify-center text-xl">
                  {file.type.startsWith('video/') ? '🎬' : '🎵'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                  </p>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-gray-500 hover:text-red-400"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Button */}
      {files.length > 0 && (
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={() => setUploading(true)}
            disabled={uploading}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-bold text-lg disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Studio;
